import { z } from 'zod';

// T-170 Fase 1d — Fonte di verità condivisa per la coda import documenti.
// Lo shape di `result` è esattamente il DryRunResponse prodotto dal worker
// (fiscal-photo analyzeDocument) e consumato dalla UI di review esistente.

export const DOCUMENT_JOB_STATUSES = [
  'queued',
  'analyzing',
  'ready',
  'committed',
  'failed',
  'dismissed',
] as const;
export const DocumentJobStatusSchema = z.enum(DOCUMENT_JOB_STATUSES);
export type DocumentJobStatus = z.infer<typeof DocumentJobStatusSchema>;

/** Stati terminali: non più drenabili/processabili. */
export function isTerminalJobStatus(s: DocumentJobStatus): boolean {
  return s === 'committed' || s === 'dismissed';
}
/** Stati per cui la UI continua a fare polling (il job può ancora cambiare). */
export function isActiveJobStatus(s: DocumentJobStatus): boolean {
  return s === 'queued' || s === 'analyzing';
}

// Stato del draft locale (client-side, SQLCipher) — vita del documento prima
// che il job server esista (P4: persistito prima del primo upload).
export const DRAFT_STATES = [
  'capturing',
  'uploading',
  'enqueue_pending',
  'enqueued',
  'done',
  'discarded',
] as const;
export const DraftStateSchema = z.enum(DRAFT_STATES);
export type DraftState = z.infer<typeof DraftStateSchema>;

// DryRunResponse — ciò che il worker scrive in document_jobs.result e che la
// review idrata. passthrough: la review legge campi annidati (lines[].food_match,
// alias_meta, current_lot…) il cui dettaglio non va ri-vincolato qui.
export const DryRunResponseSchema = z
  .object({
    mode: z.literal('dry_run'),
    provider_used: z.string().optional(),
    confidence: z.number().nullable().optional(),
    request_id: z.string().optional(),
    document: z
      .object({
        number: z.string().nullable(),
        date: z.string().nullable(),
        total_amount: z.number().nullable(),
        type: z.string(),
        currency: z.string(),
      })
      .passthrough(),
    supplier: z
      .object({
        parsed: z.unknown().nullable(),
        existing: z.unknown().nullable(),
      })
      .passthrough(),
    lines: z.array(z.record(z.unknown())),
    existing_document: z.unknown().nullable().optional(),
  })
  .passthrough();
export type DryRunResponse = z.infer<typeof DryRunResponseSchema>;

// Duplicato (warning, non blocco): doc esistente con stessa chiave logica.
export const DocumentDuplicateSchema = z
  .object({
    source: z.enum(['public', 'fiscal']),
    id: z.string(),
    number: z.string(),
    date: z.string(),
    type: z.string().optional(),
  })
  .nullable();

// Riga document_jobs come letta dal client in polling.
export const DocumentJobRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  client_draft_uuid: z.string().uuid().optional(),
  status: DocumentJobStatusSchema,
  step: z.string().nullable().optional(),
  page_paths: z.array(z.string()).default([]),
  doc_kind_guess: z.enum(['ddt', 'fattura']).nullable().optional(),
  result: DryRunResponseSchema.nullable().optional(),
  duplicate: DocumentDuplicateSchema.optional(),
  error: z
    .object({ code: z.string(), message: z.string().optional() })
    .nullable()
    .optional(),
  attempts: z.number().int().nonnegative().default(0),
  committed_document_id: z.string().uuid().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().optional(),
});
export type DocumentJobRow = z.infer<typeof DocumentJobRowSchema>;
